import {Link} from 'react-router-dom';
import {TextField, TextArea, SegmentsEditor, Field, type SectionProps} from '../controls';
import {accessCodeEntryMode, groupRequiresAccessCode} from '@common/enrollmentSite/types';
import {MirrorLock} from '../mirrorLock';

export function GateSection({config, patch, groupSlug, isNew}: SectionProps) {
  const gated = groupRequiresAccessCode(config);
  const screen = config.accessCodeScreen;
  const dropdown = accessCodeEntryMode(config) === 'dropdown';

  return (
    <div className='space-y-5'>
      <MirrorLock field='gateSettings'>
        <Field
          label='Require an access code'
          hint='Off makes the site public — the gate is skipped and anyone with the URL can enroll.'
        >
          <label className='flex items-center gap-2 text-sm'>
            {/* Gated is the default — store it as undefined (omitted), not an explicit true,
                so toggling off then on doesn't leave a spurious diff that reads as dirty. */}
            <input
              type='checkbox'
              checked={gated}
              onChange={(e) => patch({requiresAccessCode: e.target.checked ? undefined : false})}
            />
            Require code/location to enter
          </label>
        </Field>
      </MirrorLock>

      <Field
        label='Demo site'
        hint='For sales/marketing. Skips the gate, and nothing is submitted to e123 — the flow ends on a sample confirmation.'
      >
        <label className='flex items-center gap-2 text-sm'>
          {/* Off is the default — store it omitted, not an explicit false. */}
          <input
            type='checkbox'
            checked={!!config.isDemo}
            onChange={(e) => patch({isDemo: e.target.checked ? true : undefined})}
          />
          Make this a demo site
        </label>
      </Field>

      {gated && (
        <>
          <MirrorLock field='gateSettings'>
            <Field label='How members find their location'>
              <div className='space-y-1.5 text-sm'>
                <label className='flex items-center gap-2'>
                  {/* Code is the default — store dropdown explicitly, code as omitted. */}
                  <input
                    type='radio'
                    name='entryMode'
                    checked={!dropdown}
                    onChange={() => patch({accessCodeScreen: {...screen, entryMode: undefined}})}
                  />
                  Access code
                </label>
                <label className='flex items-center gap-2'>
                  <input
                    type='radio'
                    name='entryMode'
                    checked={dropdown}
                    onChange={() => patch({accessCodeScreen: {...screen, entryMode: 'dropdown'}})}
                  />
                  Location dropdown
                </label>
              </div>
              <p className='text-xs text-muted-foreground'>
                Both use the same locations, managed under{' '}
                {isNew ? (
                  'Manage access codes (after the first save)'
                ) : (
                  <Link to={`/enrollment-sites/${groupSlug}/locations`} className='underline'>
                    Manage access codes
                  </Link>
                )}
                . Each location carries the code and the e123 agent its enrollments post under.
                {dropdown &&
                  ' The dropdown lists every active location by name, so anyone with access to the URL can access all locations.'}
              </p>
            </Field>
          </MirrorLock>
          {!dropdown && (
            <MirrorLock field='gateSettings'>
              <TextField
                label='Access-code term'
                hint='What the code is called in copy — falls back to "access code".'
                value={config.accessCodeTerm}
                onChange={(accessCodeTerm) => patch({accessCodeTerm: accessCodeTerm || undefined})}
              />
            </MirrorLock>
          )}
          <MirrorLock field='gateHeadline'>
            <SegmentsEditor
              label='Gate headline'
              hint='The H1 on the entry screen. Mark any run as accent-colored.'
              segments={screen.headline ?? []}
              onChange={(headline) => patch({accessCodeScreen: {...screen, headline}})}
            />
          </MirrorLock>
          <MirrorLock field='gateBlurb'>
            <TextArea
              label='Lead text'
              hint='Intro paragraph on the gate screen.'
              value={screen.blurb}
              onChange={(blurb) => patch({accessCodeScreen: {...screen, blurb}})}
            />
          </MirrorLock>
          {!dropdown && (
            <MirrorLock field='gateSettings'>
              <TextField
                label='Input placeholder'
                hint='Placeholder inside the code input, e.g. "e.g., 10431".'
                value={screen.placeholder}
                onChange={(placeholder) => patch({accessCodeScreen: {...screen, placeholder}})}
              />
            </MirrorLock>
          )}
        </>
      )}
    </div>
  );
}
